import { cn } from "@/lib/utils";
import { Loader2, Crown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { LanguageFlags } from "./LanguageFlags";
import { WordSearchResult } from "@/hooks/useWordSearch";
import { AdminDeleteWordButton } from "./AdminDeleteWordButton";

interface SearchResultsProps {
  results: WordSearchResult[];
  isLoading: boolean;
  error: string | null;
  onResultClick?: (word: string) => void;
  onWordDeleted?: (word: string) => void;
}

const FREE_RESULT_LIMIT = 5;

export const SearchResults = ({ results, isLoading, error, onResultClick, onWordDeleted }: SearchResultsProps) => {
  const { isPremium, isAuthenticated, isAdmin } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Söker...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  if (results.length === 0) {
    return null;
  }

  const shouldLimit = !isPremium;
  const displayResults = shouldLimit ? results.slice(0, FREE_RESULT_LIMIT) : results;
  const hiddenCount = shouldLimit ? Math.max(0, results.length - FREE_RESULT_LIMIT) : 0;

  return (
    <div className="mt-8">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">
        {results.length} resultat hittades
        {shouldLimit && results.length > FREE_RESULT_LIMIT && (
          <span className="text-xs ml-2">(visar {FREE_RESULT_LIMIT})</span>
        )}
      </h3>
      <div className="space-y-2">
        {displayResults.map((result, index) => (
          <div
            key={index}
            onClick={() => onResultClick?.(result.word)}
            className={cn(
              "px-4 py-3 rounded-lg",
              "bg-secondary/50 text-foreground",
              "border border-border/50",
              "hover:bg-secondary transition-colors",
              onResultClick && "cursor-pointer hover:border-primary/50"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">
                  {result.word}
                </span>
                <LanguageFlags language={result.language} />
              </div>
              {isAdmin && (
                <AdminDeleteWordButton 
                  word={result.word}
                  onDeleted={() => onWordDeleted?.(result.word)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Premium upsell for limited results */}
      {shouldLimit && hiddenCount > 0 && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
          <Crown className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">+{hiddenCount} fler resultat</span>
            {" – "}
            {isAuthenticated 
              ? "Uppgradera till premium för att se alla resultat."
              : "Logga in och uppgradera för att se alla resultat."
            }
          </span>
        </div>
      )}
    </div>
  );
};
